import * as React from "react";
import { Box, IconButton, Stack, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import sild1 from "../assets/img/flash image 1.png";
// import sild2 from "../assets/img/flash image 2.png";
import sild2 from "../assets/IITGROUP.png";
import sild3 from "../assets/img/flash image 3.png";
import sild4 from "../assets/img/flash image 4.png";
import sild5 from "../assets/img/flash image 2.png";
const slides = [sild1, sild2, sild3, sild4, sild5];

export default function ImageOnlySlider() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md")); // md+ desktop
  return isDesktop ? <DesktopSlider /> : <MobileTabletSlider />;
}

/* -------------------- MOBILE + TABLET -------------------- */
function MobileTabletSlider() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const [index, setIndex] = React.useState(0);
  const [paused, setPaused] = React.useState(false);

  const clamp = React.useCallback((i) => (i + slides.length) % slides.length, []);
  const next = React.useCallback(() => setIndex((p) => clamp(p + 1)), [clamp]);
  const prev = React.useCallback(() => setIndex((p) => clamp(p - 1)), [clamp]);

  React.useEffect(() => {
    if (paused || reduceMotion) return;
    const id = setInterval(next, 4500);
    return () => clearInterval(id);
  }, [paused, reduceMotion, next]);

  const touchStartX = React.useRef(null);
  const touchDeltaX = React.useRef(0);

  const onTouchStart = (e) => {
    setPaused(true);
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
    touchDeltaX.current = 0;
  };
  const onTouchMove = (e) => {
    if (touchStartX.current == null) return;
    const x = e.touches?.[0]?.clientX ?? touchStartX.current;
    touchDeltaX.current = x - touchStartX.current;
  };
  const onTouchEnd = () => {
    const dx = touchDeltaX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 60) dx < 0 ? next() : prev();
    setPaused(false);
  };

  const heroMinH = isMobile ? 320 : isTablet ? 420 : 520;

  return (
    <Box
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      sx={{
        position: "relative",
        width: "100%",
        minHeight: heroMinH,
        overflow: "hidden",
        background:
          "linear-gradient(135deg, #0b2a5a 0%, #061a2e 55%, #06121f 100%)",
      }}
    >
      {/* glow */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(900px 420px at 20% 25%, rgba(255,255,255,0.10), transparent 60%), radial-gradient(700px 380px at 80% 65%, rgba(120,180,255,0.10), transparent 55%)",
        }}
      />

      {/* TRACK */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          width: "100%",
          height: "100%",
          transform: `translate3d(-${index * 100}%, 0, 0)`,
          transition: reduceMotion
            ? "none"
            : "transform 750ms cubic-bezier(.2,.9,.2,1)",
          willChange: "transform",
          backfaceVisibility: "hidden",
        }}
      >
        {slides.map((img, i) => (
          <Box
            key={i}
            sx={{
              flex: "0 0 100%",
              width: "100%",
              height: "100%",
              display: "grid",
              placeItems: "center",
              px: { xs: 1.25, sm: 2.5 },
              py: { xs: 2.25, sm: 3 },
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: { xs: 560, sm: 920 },
                height: "auto",
                aspectRatio: { xs: "16 / 10", sm: "16 / 9" },
                borderRadius: { xs: 4, sm: 5 },
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.20)",
                boxShadow: "0 14px 44px rgba(0,0,0,0.45)",
                background: "rgba(255,255,255,0.03)",
                position: "relative",
              }}
            >
              <Box
                component="img"
                src={img}
                alt={`Slide ${i + 1}`}
                draggable={false}
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  objectFit: "contain",
                  backgroundColor: "rgba(0,0,0,0.22)",
                  userSelect: "none",
                  pointerEvents: "none",
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>

      {/* DOTS */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          position: "absolute",
          bottom: { xs: 10, sm: 14 },
          left: "50%",
          transform: "translateX(-50%)",
          px: 1.5,
          py: 1,
          borderRadius: 999,
          bgcolor: "rgba(0,0,0,0.28)",
          border: "1px solid rgba(255,255,255,0.14)",
          backdropFilter: "blur(8px)",
          zIndex: 6,
        }}
      >
        {slides.map((_, d) => {
          const active = d === index;
          return (
            <Box
              key={d}
              onClick={() => setIndex(d)}
              sx={{
                width: active ? 26 : 10,
                height: 10,
                borderRadius: 999,
                cursor: "pointer",
                transition: "all 220ms ease",
                bgcolor: active ? "#fff" : "rgba(255,255,255,0.50)",
              }}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

/* -------------------- DESKTOP ONLY (md+) -------------------- */
function DesktopSlider() {
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const isWide = useMediaQuery("(min-width:1645px)"); // ✅ 1645px+
  const isMaxWide = useMediaQuery("(max-width:1950px)"); // ✅ upto 1950px
  const useWideFrame = isWide && isMaxWide; // ✅ 1645–1950 special layout

  const [index, setIndex] = React.useState(0);
  const [paused, setPaused] = React.useState(false);

  const clamp = React.useCallback((i) => (i + slides.length) % slides.length, []);
  const next = React.useCallback(() => setIndex((p) => clamp(p + 1)), [clamp]);
  const prev = React.useCallback(() => setIndex((p) => clamp(p - 1)), [clamp]);

  React.useEffect(() => {
    if (paused || reduceMotion) return;
    const id = setInterval(next, 4500);
    return () => clearInterval(id);
  }, [paused, reduceMotion, next]);

  return (
    <Box
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      sx={{
        position: "relative",
        width: "100%",
        height: "calc(100vh - var(--nav-h, 0px))",
        minHeight: 560,
        overflow: "hidden",
        background: "#06121f",
      }}
      marginTop={"-90px"} // aad some
    >
      {/* TRACK */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          width: "100%",
          height: "100%",
          transform: `translate3d(-${index * 100}%, 0, 0)`,
          transition: reduceMotion ? "none" : "transform 800ms ease",
          willChange: "transform",
        }}
      >
        {slides.map((img, i) => (
          <Box
            key={i}
            sx={{
              flex: "0 0 100%",
              width: "100%",
              height: "100%",
              display: "grid",
              placeItems: "center",
            }}
          >
            {/* ✅ 1645px–1950px: framed + border radius + still NO CROP */}
            {useWideFrame ? (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  maxWidth: 1950,           // ✅ keep premium frame for ultrawide
                  px: 3,
                  py: 3,
                  boxSizing: "border-box",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 4, // ✅ radius
                    overflow: "hidden",
                    border: "1px solid rgba(255,255,255,0.18)", // ✅ border
                    boxShadow: "0 28px 90px rgba(0,0,0,0.55)",
                    background: "rgba(255,255,255,0.03)",
                  }}
                >
                  <Box
                    component="img"
                    src={img}
                    alt={`Slide ${i + 1}`}
                    draggable={false}
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "block",
                      objectFit: "contain", // ✅ NO CROP
                      backgroundColor: "#06121f",
                      userSelect: "none",
                      pointerEvents: "none",
                    }}
                  />
                </Box>
              </Box>
            ) : (
              /* ✅ <=1645px (your current desktop look): full area, NO border/radius */
              <Box
                component="img"
                src={img}
                alt={`Slide ${i + 1}`}
                draggable={false}
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  objectFit: "contain", // ✅ NO CROP
                  backgroundColor: "#06121f",
                  userSelect: "none",
                  pointerEvents: "none",
                }}
              />
            )}
          </Box>
        ))}
      </Box>

      {/* ARROWS */}
      <IconButton
        onClick={prev}
        aria-label="Previous"
        sx={{
          position: "absolute",
          left: 18,
          top: "50%",
          transform: "translateY(-50%)",
          color: "#fff",
          bgcolor: "rgba(0,0,0,0.35)",
          border: "1px solid rgba(255,255,255,0.18)",
          backdropFilter: "blur(8px)",
          "&:hover": { bgcolor: "rgba(0,0,0,0.55)" },
          zIndex: 5,
        }}
      >
        <ArrowBackIosNewIcon fontSize="small" />
      </IconButton>

      <IconButton
        onClick={next}
        aria-label="Next"
        sx={{
          position: "absolute",
          right: 18,
          top: "50%",
          transform: "translateY(-50%)",
          color: "#fff",
          bgcolor: "rgba(0,0,0,0.35)",
          border: "1px solid rgba(255,255,255,0.18)",
          backdropFilter: "blur(8px)",
          "&:hover": { bgcolor: "rgba(0,0,0,0.55)" },
          zIndex: 5,
        }}
      >
        <ArrowForwardIosIcon fontSize="small" />
      </IconButton>

      {/* DOTS */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          position: "absolute",
          bottom: 18,
          left: "50%",
          transform: "translateX(-50%)",
          px: 1.5,
          py: 1,
          borderRadius: 999,
          bgcolor: "rgba(0,0,0,0.28)",
          border: "1px solid rgba(255,255,255,0.14)",
          backdropFilter: "blur(8px)",
          zIndex: 6,
        }}
      >
        {slides.map((_, d) => {
          const active = d === index;
          return (
            <Box
              key={d}
              onClick={() => setIndex(d)}
              sx={{
                width: active ? 26 : 10,
                height: 10,
                borderRadius: 999,
                cursor: "pointer",
                transition: "all 220ms ease",
                bgcolor: active ? "#fff" : "rgba(255,255,255,0.50)",
              }}
            />
          );
        })}
      </Stack>
    </Box>
  );
}
